import React from 'react';
import { CheckCircle, Target, Award, Heart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { companyInfo, technologies } from '../data/mock';
import AnimatedSection from './AnimatedSection';

const About = () => {
  const values = [
    {
      icon: Target,
      title: "Innovation-Driven",
      description: "We stay ahead of technology trends to deliver cutting-edge solutions that give our clients a competitive advantage."
    },
    {
      icon: Award,
      title: "Quality Excellence",
      description: "Our commitment to quality ensures robust, scalable, and maintainable software that stands the test of time."
    },
    {
      icon: Heart,
      title: "Client-Centric",
      description: "We build lasting partnerships with our clients, understanding their needs and delivering solutions that exceed expectations."
    },
    {
      icon: CheckCircle,
      title: "Reliable Delivery",
      description: "We pride ourselves on delivering projects on time and within budget while maintaining the highest quality standards."
    }
  ];

  const groupedTechnologies = technologies.reduce((acc, tech) => {
    if (!acc[tech.category]) {
      acc[tech.category] = [];
    }
    acc[tech.category].push(tech.name);
    return acc;
  }, {});

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <AnimatedSection animation="fadeInUp">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About <span className="text-blue-900 animate-pulse">MYLKSYSTEMS</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {companyInfo.description}
            </p>
          </div>
        </AnimatedSection>

        {/* Company Story */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <AnimatedSection delay={200} animation="fadeInLeft">
            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-word">Our Story</h3>
              <p className="text-gray-600 leading-relaxed text-lg animate-fade-in-word" style={{ animationDelay: '0.2s' }}>
                Founded in {companyInfo.established} in {companyInfo.location}, MYLKSYSTEMS has grown from a small team of 
                passionate developers to a leading software development company serving clients across Sri Lanka and internationally.
              </p>
              <p className="text-gray-600 leading-relaxed text-lg animate-fade-in-word" style={{ animationDelay: '0.4s' }}>
                Our journey began with a simple mission: to help businesses harness the power of technology to achieve their goals. 
                Today, we continue that mission by delivering innovative software solutions that drive digital transformation.
              </p>
              <p className="text-gray-600 leading-relaxed text-lg animate-fade-in-word" style={{ animationDelay: '0.6s' }}>
                With over {companyInfo.established && new Date().getFullYear() - parseInt(companyInfo.established)} years of experience, 
                we've successfully delivered 50+ projects, helping businesses streamline operations, improve efficiency, and accelerate growth.
              </p>
              
              {/* Key Achievements */}
              <div className="grid grid-cols-2 gap-4 mt-8">
                {[
                  { value: "50+", label: "Projects Delivered" },
                  { value: "35+", label: "Happy Clients" },
                  { value: "15+", label: "Expert Team Members" },
                  { value: "6+", label: "Years of Excellence" }
                ].map((stat, index) => (
                  <div 
                    key={index}
                    className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105 animate-fade-in-badge"
                    style={{ animationDelay: `${index * 0.1 + 0.8}s` }}
                  >
                    <div className="text-2xl font-bold text-blue-900 mb-1 animate-count-up">{stat.value}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={400} animation="fadeInRight">
            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-word">Our Mission & Vision</h3>
              
              <Card className="border border-gray-200 bg-white shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-105 animate-fade-in-up">
                <CardHeader className="pb-3">
                  <CardTitle className="text-xl text-blue-900 flex items-center">
                    <Target className="w-5 h-5 mr-2 animate-pulse" />
                    Mission
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed">
                    To empower businesses with innovative software solutions that drive growth, efficiency, and digital transformation 
                    while building lasting partnerships based on trust, quality, and excellence.
                  </p>
                </CardContent>
              </Card>

              <Card className="border border-gray-200 bg-white shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-105 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-xl text-blue-900 flex items-center">
                    <Award className="w-5 h-5 mr-2 animate-pulse" />
                    Vision
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed">
                    To be the leading software development partner in Sri Lanka, recognized for our innovative solutions, 
                    technical expertise, and commitment to helping businesses thrive in the digital age.
                  </p>
                </CardContent>
              </Card>
            </div>
          </AnimatedSection>
        </div>

        {/* Our Values */}
        <AnimatedSection delay={600} animation="fadeInUp">
          <div className="mb-20">
            <h3 className="text-3xl font-bold text-gray-900 text-center mb-12 animate-fade-in-word">Our Core Values</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => {
                const IconComponent = value.icon;
                return (
                  <AnimatedSection key={index} delay={index * 150} animation="scaleIn">
                    <Card className="text-center group card-hover-effect border border-gray-200 bg-white">
                      <CardHeader className="pb-4">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                          <IconComponent className="h-8 w-8 text-blue-900 group-hover:animate-pulse" />
                        </div>
                        <CardTitle className="text-lg font-bold text-gray-900 group-hover:text-blue-900 transition-colors duration-300">
                          {value.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-600 leading-relaxed text-sm">
                          {value.description}
                        </p>
                      </CardContent>
                    </Card>
                  </AnimatedSection>
                );
              })}
            </div>
          </div>
        </AnimatedSection>

        {/* Technology Stack */}
        <AnimatedSection delay={800} animation="scaleIn">
          <div className="bg-white rounded-2xl p-8 md:p-12 border border-gray-200 shadow-sm relative overflow-hidden">
            {/* Animated background elements */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-32 h-32 bg-blue-400 rounded-full animate-pulse"></div>
              <div className="absolute bottom-0 right-0 w-24 h-24 bg-yellow-400 rounded-full animate-bounce-subtle" style={{ animationDelay: '1s' }}></div>
            </div>
            
            <div className="relative z-10">
              <h3 className="text-3xl font-bold text-gray-900 text-center mb-8 animate-fade-in-word">Our Technology Stack</h3>
              <p className="text-gray-600 text-center mb-12 text-lg max-w-3xl mx-auto animate-fade-in-word" style={{ animationDelay: '0.2s' }}>
                We work with cutting-edge technologies to deliver robust, scalable, and future-ready solutions for our clients.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
                {Object.entries(groupedTechnologies).map(([category, techs], categoryIndex) => (
                  <AnimatedSection key={category} delay={categoryIndex * 100} animation="fadeInUp">
                    <div className="text-center">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-blue-900 animate-fade-in-word">
                        {category}
                      </h4>
                      <div className="space-y-2">
                        {techs.map((tech, techIndex) => (
                          <div 
                            key={techIndex}
                            className="text-gray-600 py-2 px-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-200 hover:scale-105 animate-fade-in-badge"
                            style={{ animationDelay: `${techIndex * 0.1 + 0.4}s` }}
                          >
                            {tech}
                          </div>
                        ))}
                      </div>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default About;